import NetworkPingIcon from '@mui/icons-material/NetworkPing';
import { Tooltip, Typography } from '@mui/material';
import { green, red } from '@mui/material/colors';
import { styled } from '@mui/material/styles';
import LogoPJeSimboloBranco from '@/img/LogoPJeSimboloBranco';
import { makeObservable, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import ErrorUtil from '@/util/ErrorUtil';
import Ambiente, { TIPO_AMBIENTE } from '../util/Ambiente';
import ReactComponent from './ReactComponent';

const corConectividadeOK = green.A100;
const corConectividadeNaoOK = red.A100;

const KeyframesBlink = styled('span')({
    '@keyframes blink': {
        from: {
            opacity: 1
        },
        to: {
            opacity: 0.3
        }
    },
    animation: 'blink 0.5s',
    animationIterationCount: 15
});

class FooterApp extends ReactComponent {
    versaoPJe = '';
    informacoesApp = '';

    constructor(props) {
        super(props);

        makeObservable(this, {
            versaoPJe: observable,
            informacoesApp: observable
        });
    }

    async componentDidMount() {
        try {
            if (!Ambiente.isAmbienteInicializado()) {
                await Ambiente.esperarInicializacaoAmbiente();
            }

            let versaoOrigemResponse = await this.versaoOrigemService.versaoPJe();
            let variaveisAmbiente = await this.healthCheckService.carregaVariaveisAmbiente();
            runInAction(() => {
                this.versaoPJe = versaoOrigemResponse;
                this.informacoesApp = variaveisAmbiente;
            });
        } catch (error) {
            console.error(`Falha na inicialização do componente FooterApp: ${ErrorUtil.getMensagemErro(error)}`);
            throw new Error('Falha na inicialização do componente FooterApp', { cause: error });
        }
    }

    renderInfoVersaoPJe() {
        return (
            <>
                <Typography marginRight={'5px'}>Integrado ao</Typography>
                <LogoPJeSimboloBranco
                    transform="scale(1.6)"
                    alt={'Logo do PJe'}
                />
                <Typography marginLeft={'3px'}>{this.versaoPJe}</Typography>
            </>
        );
    }

    renderInfoVersao() {
        return (
            <Tooltip
                placement="top"
                title={`Versão (front-end): ${this.informacoesApp.versaoAplicacao} ${this.informacoesApp.buildAplicacao}`}
            >
                <Typography>
                    {this.informacoesApp.nomeAplicacao} - Versão: {Ambiente.getBuildInfo().numeroVersao}
                    &nbsp;de&nbsp;
                    {Ambiente.getBuildInfo().dataVersao}
                </Typography>
            </Tooltip>
        );
    }

    renderInfoAmbiente() {
        if (
            Ambiente.getDescricao() === TIPO_AMBIENTE.PRD.descricao ||
            Ambiente.getDescricao() === TIPO_AMBIENTE.PRD_2GRAU.descricao
        ) {
            if (!(window.location.host.includes('local') || window.location.host.includes('127.'))) {
                return null;
            }
        }

        return (
            <>
                <Typography
                    component="div"
                    display={'inline'}
                >
                    &nbsp;&nbsp;
                </Typography>
                <Typography
                    display={'inline'}
                    sx={{ fontWeight: 'bold', backgroundColor: 'red' }}
                >
                    {Ambiente.getDescricao()}
                </Typography>
            </>
        );
    }

    renderInfoSucessoConectividade() {
        const msgVerificacaoConectividade = 'Conexão de rede disponível';
        //const msgValidacaoConectitividadeServidor = 'Conectividade com os computadores de destino verificada e validada.'; // [TODO] Usar essa mensagem ao implementar o teste de conectividade com o backend

        return (
            <>
                <Typography
                    fontSize="13px"
                    color="inherit"
                >
                    {msgVerificacaoConectividade}
                </Typography>
                <NetworkPingIcon sx={{ color: corConectividadeOK }} />
            </>
        );
    }

    renderInfoFalhaConectividade() {
        const msgFalhaVerificacaoConectividade =
            'Não foi possível estabelecer uma conexão com os computadores de destino. Favor verificar as conexões de rede locais ou contactar o suporte. Sistema operando em modo off-line.';

        return (
            <>
                <Typography
                    fontSize="13px"
                    color="inherit"
                >
                    {msgFalhaVerificacaoConectividade}
                </Typography>
                <NetworkPingIcon sx={{ color: corConectividadeNaoOK }} />
            </>
        );
    }

    renderInfoConectividade() {
        const espacoEntreIconeTexto = '5px';

        if (this.appStore.conectividadeOK) {
            return (
                <>
                    <Tooltip
                        placement="top"
                        title={this.renderInfoSucessoConectividade()}
                    >
                        <NetworkPingIcon sx={{ verticalAlign: 'bottom', color: corConectividadeOK }} />
                    </Tooltip>
                    {this.appStore.exibirTextoConectividadeOK && ( // Exibe o texto apenas na mudança de estado (de offline para online)
                        <Tooltip
                            placement="top"
                            title={this.renderInfoSucessoConectividade()}
                        >
                            <Typography
                                display="inline"
                                style={{ marginLeft: espacoEntreIconeTexto }}
                                color={corConectividadeOK}
                            >
                                On-line
                            </Typography>
                        </Tooltip>
                    )}
                </>
            );
        } else {
            return (
                <>
                    <Tooltip
                        placement="top"
                        title={this.renderInfoFalhaConectividade()}
                    >
                        <KeyframesBlink>
                            <NetworkPingIcon
                                display="inline"
                                sx={{ verticalAlign: 'bottom', color: corConectividadeNaoOK }}
                            />
                        </KeyframesBlink>
                    </Tooltip>
                    <Tooltip
                        placement="top"
                        title={this.renderInfoFalhaConectividade()}
                    >
                        <KeyframesBlink>
                            <Typography
                                display="inline"
                                style={{ marginLeft: espacoEntreIconeTexto }}
                                color={corConectividadeNaoOK}
                            >
                                Off-line
                            </Typography>
                        </KeyframesBlink>
                    </Tooltip>
                </>
            );
        }
    }

    render() {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: 'white',
                    backgroundColor: 'grey',
                    padding: '1px 6px'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center' }}>{this.renderInfoVersaoPJe()}</div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {this.renderInfoVersao()}
                    {this.renderInfoAmbiente()}
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>{this.renderInfoConectividade()}</div>
            </div>
        );
    }
}

export default inject('stores')(observer(FooterApp));
